import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LogOut,
  Search,
  RefreshCw,
  Download,
  Trash2,
  Mail,
  Phone,
  Building2,
  ShieldCheck,
  Inbox,
  Users,
  TrendingUp,
  Eye,
  X,
} from "lucide-react";
import {
  adminLogin,
  clearAdminAuth,
  getAdminAuth,
  fetchStats,
  fetchLeads,
  fetchContactMessages,
  deleteLead,
} from "@/lib/admin";

export default function Admin() {
  const [authed, setAuthed] = useState(Boolean(getAdminAuth()));

  return (
    <div data-testid="page-admin" className="min-h-screen bg-slate-100">
      {!authed ? (
        <LoginCard onSuccess={() => setAuthed(true)} />
      ) : (
        <Dashboard onLogout={() => { clearAdminAuth(); setAuthed(false); }} />
      )}
    </div>
  );
}

function LoginCard({ onSuccess }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) return toast.error("Enter the admin password");
    setLoading(true);
    try {
      await adminLogin(username, password);
      toast.success("Welcome back");
      onSuccess();
    } catch (err) {
      const msg = err?.response?.status === 401 ? "Invalid credentials" : "Could not sign in";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <form
        data-testid="admin-login-form"
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white border border-slate-200 rounded-md shadow-xl p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-sm bg-[#0A1F44] text-white flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#1E40AF] font-semibold">Admin</p>
            <p className="font-display text-xl text-[#0A1F44]">Sozos Global Services</p>
          </div>
        </div>
        <h1 className="font-display text-2xl text-[#0A1F44] mb-2">Sign in to dashboard</h1>
        <p className="text-sm text-slate-600 mb-6">Only authorized team members can view leads.</p>

        <label className="block mb-4">
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-600 mb-1.5 block">Username</span>
          <input
            data-testid="admin-input-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full border border-slate-300 rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#1E40AF] focus:ring-2 focus:ring-blue-100"
            autoComplete="username"
          />
        </label>
        <label className="block mb-6">
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-600 mb-1.5 block">Password</span>
          <input
            data-testid="admin-input-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-slate-300 rounded-sm px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#1E40AF] focus:ring-2 focus:ring-blue-100"
            autoComplete="current-password"
          />
        </label>
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="btn-primary w-full disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-md p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-sm bg-blue-50 text-[#1E40AF] flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
        <div className="font-display text-3xl text-[#0A1F44] leading-tight mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, ls, ms] = await Promise.all([
        fetchStats(),
        fetchLeads({ q }),
        fetchContactMessages(),
      ]);
      setStats(s);
      setLeads(ls);
      setMessages(ms);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Could not load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, ls, ms] = await Promise.all([
          fetchStats(),
          fetchLeads({ q: "" }),
          fetchContactMessages(),
        ]);
        if (cancelled) return;
        setStats(s);
        setLeads(ls);
        setMessages(ms);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          toast.error("Session expired");
          onLogout();
        } else {
          toast.error("Could not load data");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onLogout]);

  const filteredLeads = leads;

  const onSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ls = await fetchLeads({ q });
      setLeads(ls);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await deleteLead(id);
      setLeads((arr) => arr.filter((l) => l.id !== id));
      setSelected(null);
      toast.success("Lead deleted");
    } catch {
      toast.error("Could not delete lead");
    }
  };

  const exportCSV = () => {
    // Neutralize CSV formula injection: prefix cells starting with =, +, -, @, tab or CR with a single quote.
    const sanitize = (v) => {
      const s = String(v ?? "");
      return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
    };
    const rows = [["Created", "Name", "Email", "Company", "Phone", "Service", "Industry", "Message", "Source"]];
    filteredLeads.forEach((l) => {
      rows.push([
        new Date(l.created_at).toISOString(),
        l.name, l.email, l.company,
        l.phone || "", l.service_interest || "", l.industry || "",
        (l.message || "").replace(/\n/g, " "), l.source || "",
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${sanitize(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sozos-leads-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="bg-[#0A1F44] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-white text-[#0A1F44] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-blue-300 font-semibold">Sozos Admin</p>
              <p className="font-display text-lg">Lead Dashboard</p>
            </div>
          </div>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 rounded-sm text-sm hover:bg-white hover:text-[#0A1F44] transition"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-8 md:py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard testid="stat-leads-total" label="Total Leads" value={stats?.leads_total ?? "—"} icon={Users} />
          <StatCard testid="stat-leads-7d" label="Leads (last 7 days)" value={stats?.leads_last_7_days ?? "—"} icon={TrendingUp} />
          <StatCard testid="stat-contact-total" label="Contact Messages" value={stats?.contact_messages_total ?? "—"} icon={Inbox} />
        </div>

        <div className="flex items-center gap-1 border-b border-slate-200 mb-6">
          {[
            { id: "leads", label: "Leads", count: leads.length },
            { id: "messages", label: "Contact Messages", count: messages.length },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-semibold tracking-wide border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-[#1E40AF] text-[#0A1F44]" : "border-transparent text-slate-500 hover:text-[#0A1F44]"
              }`}
            >
              {t.label}
              <span className="ml-2 text-xs text-slate-500">({t.count})</span>
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-[#0A1F44]"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            {tab === "leads" && (
              <button
                data-testid="admin-export-csv"
                onClick={exportCSV}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-[#0A1F44]"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            )}
          </div>
        </div>

        {tab === "leads" && (
          <>
            <form onSubmit={onSearch} className="flex items-center gap-2 mb-4">
              <div className="relative flex-1 max-w-xl">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="admin-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, email, company, message…"
                  className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#1E40AF] focus:ring-2 focus:ring-blue-100"
                />
              </div>
              <button data-testid="admin-search-submit" type="submit" className="btn-secondary py-2.5">Search</button>
              {q && (
                <button
                  type="button"
                  data-testid="admin-search-clear"
                  onClick={async () => { setQ(""); setLoading(true); const ls = await fetchLeads({ q: "" }); setLeads(ls); setLoading(false); }}
                  className="text-sm text-slate-500 hover:text-[#0A1F44]"
                >
                  Clear
                </button>
              )}
            </form>
            <LeadsTable leads={filteredLeads} onSelect={setSelected} loading={loading} />
          </>
        )}

        {tab === "messages" && (
          <MessagesTable messages={messages} loading={loading} />
        )}
      </main>

      {selected && <LeadDetail lead={selected} onClose={() => setSelected(null)} onDelete={onDelete} />}
    </div>
  );
}

function LeadsTable({ leads, onSelect, loading }) {
  if (loading && leads.length === 0) {
    return <div data-testid="leads-empty" className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500">Loading leads…</div>;
  }
  if (!leads.length) {
    return (
      <div data-testid="leads-empty" className="bg-white border border-slate-200 rounded-md p-12 text-center">
        <Inbox className="w-8 h-8 mx-auto text-slate-300" />
        <p className="mt-3 text-slate-500 text-sm">No leads yet. Submissions from the website will appear here.</p>
      </div>
    );
  }
  return (
    <div data-testid="leads-table-wrap" className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <Th>Received</Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Company</Th>
              <Th>Service</Th>
              <Th>Industry</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                <Td className="whitespace-nowrap text-slate-500 text-xs">{new Date(l.created_at).toLocaleString()}</Td>
                <Td className="font-medium text-[#0A1F44]">{l.name}</Td>
                <Td><a href={`mailto:${l.email}`} className="text-[#1E40AF] hover:underline">{l.email}</a></Td>
                <Td>{l.company}</Td>
                <Td className="text-slate-600">{l.service_interest || "—"}</Td>
                <Td className="text-slate-600">{l.industry || "—"}</Td>
                <Td>
                  <button
                    data-testid={`lead-view-${l.id}`}
                    onClick={() => onSelect(l)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E40AF] hover:text-[#0A1F44]"
                  >
                    <Eye className="w-3.5 h-3.5" /> View
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MessagesTable({ messages, loading }) {
  if (loading && messages.length === 0) {
    return <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500">Loading messages…</div>;
  }
  if (!messages.length) {
    return (
      <div data-testid="messages-empty" className="bg-white border border-slate-200 rounded-md p-12 text-center">
        <Inbox className="w-8 h-8 mx-auto text-slate-300" />
        <p className="mt-3 text-slate-500 text-sm">No contact messages yet.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <Th>Received</Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Subject</Th>
              <Th>Message</Th>
            </tr>
          </thead>
          <tbody>
            {messages.map((m) => (
              <tr key={m.id} data-testid={`message-row-${m.id}`} className="border-b border-slate-100 hover:bg-slate-50 align-top">
                <Td className="whitespace-nowrap text-slate-500 text-xs">{new Date(m.created_at).toLocaleString()}</Td>
                <Td className="font-medium text-[#0A1F44]">{m.name}</Td>
                <Td><a href={`mailto:${m.email}`} className="text-[#1E40AF] hover:underline">{m.email}</a></Td>
                <Td>{m.subject || "—"}</Td>
                <Td className="text-slate-700 max-w-md">{m.message}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold px-4 py-3">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function LeadDetail({ lead, onClose, onDelete }) {
  return (
    <div data-testid="lead-detail" className="fixed inset-0 z-[80] flex items-end md:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white max-w-xl w-full rounded-md shadow-2xl border border-slate-200 p-7">
        <button data-testid="lead-detail-close" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-[#0A1F44]" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#1E40AF] font-semibold">Lead Detail</p>
        <h3 className="font-display text-2xl text-[#0A1F44] mt-2">{lead.name}</h3>
        <p className="text-sm text-slate-500 mt-1">{new Date(lead.created_at).toLocaleString()}</p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field icon={Mail} label="Email" value={<a href={`mailto:${lead.email}`} className="text-[#1E40AF] hover:underline">{lead.email}</a>} />
          <Field icon={Phone} label="Phone" value={lead.phone || "—"} />
          <Field icon={Building2} label="Company" value={lead.company} />
          <Field icon={Users} label="Source" value={lead.source || "website"} />
          <Field icon={Inbox} label="Service" value={lead.service_interest || "—"} />
          <Field icon={ShieldCheck} label="Industry" value={lead.industry || "—"} />
        </div>

        {lead.message && (
          <div className="mt-5 pt-5 border-t border-slate-100">
            <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Message</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{lead.message}</p>
          </div>
        )}

        <div className="mt-7 flex items-center justify-between">
          <a href={`mailto:${lead.email}`} data-testid="lead-reply" className="btn-primary">
            <Mail className="w-4 h-4" /> Reply
          </a>
          <button
            data-testid="lead-delete"
            onClick={() => onDelete(lead.id)}
            className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-sm p-3">
      <div className="w-8 h-8 rounded-sm bg-white text-[#1E40AF] flex items-center justify-center border border-slate-200 shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
        <div className="text-sm text-[#0A1F44] mt-1 break-words">{value}</div>
      </div>
    </div>
  );
}
